import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import debounce from 'lodash/debounce';

import { Button, Container, Icon, InputText, yup, yupResolver } from '@dansk-metal/ui';
import { dayJS, getDatePart } from '@dansk-metal/utils/date';

import { Form, FormFooter, FormHeader, FormWrapper } from '@web/components/form-wrapper/form-wrapper';
import { Modal } from '@web/components/modal/modal';
import { ThemedSkeleton } from '@web/components/themed-skeleton/themed-skeleton';

import { validationMessages } from '@web/constants/messages/validation-messages';

import { useAuthenticatedCRMRequestConfig, useGetUser } from '@web/services/crm-api/hooks';
import { CompanyDto, ErrorResult, PositionDto, useGetCompanyByFilter, useGetCompanyById, useGetPositionById, useGetPositions, usePutMember } from '@web/services/crm-api/rest';
import { crmError } from '@web/services/errorhandler/appError';

import { CreatableSelect } from '@apps/web/src/components/creatable-select/creatable-select';

import styles from './membership-workplace-info.module.scss';

const schema = yup.object({
	position: yup
		.object({
			value: yup.string(),
			label: yup.string(),
		}),
	employer: yup
		.object({
			value: yup.string(),
			label: yup.string(),
		})
		.test(
			'isValidEmployer',
			validationMessages.workplace.error,
			function isValidEmployer(value: { value: string; label: string }) {
				const isValid = !!value.value && !!value.label;
				const isNew = value.value === value.label;
				// if no workplace option is selected return valid
				if (!value.value && !!value.label) {
					return true;
				}

				if (isNew && (!this.parent.employmentAddress || !this.parent.employmentPostalCode)) {
					return false;
				}

				if (!isValid && this.parent.employmentAddress && this.parent.employmentPostalCode) {
					return false;
				}

				return true;
			},
		),
	employmentDate: yup.string().when('employer', {
		is: (employer) => !!employer?.value,
		then: (rule) => rule
			.required(validationMessages.startDate.empty)
			.test('validateRange', validationMessages.startDate.error, (value) => {
				const valueDate = dayJS.utc(new Date(value));
				const futureLimitDate = dayJS.utc('2099-12-31T23:59:59Z');
				const pastLimitDate = dayJS.utc('1900-01-01T00:00:00Z');
				// check if date is withing range
				if (valueDate.isAfter(futureLimitDate) || valueDate.isBefore(pastLimitDate)) {
					return false;
				}

				return true;
			}),
	}),
	employmentAddress: yup.string().when('employer', {
		is: (employer) => employer?.value && employer.value === employer.label && employer.value,
		then: (rule) => rule.required(validationMessages.workplace_address.empty).min(3, validationMessages.workplace_address.error)
			.max(160, validationMessages.workplace_address.error),
	}),
	employmentPostalCode: yup.string().when('employer', {
		is: (employer) => employer?.value && employer.value === employer.label && employer.value,
		then: (rule) => rule.required(validationMessages.workplace_zip_code.empty).test('validZipCode', validationMessages.workplace_zip_code.error, (value) => {
			const zipCode = Number(value);

			return zipCode >= 1000 && zipCode <= 9999;
		}),
	}),
});

type FormValues = yup.InferType<typeof schema>;

export function MembershipWorkplaceInfo() {
	const [employerSearchTerm, setEmployerSearchTerm] = useState('');
	const [positionSearchTerm, setPositionSearchTerm] = useState('');
	const [modalVisible, setModalVisible] = useState(false);
	const [dateVisible, setDateVisible] = useState(false);
	const defaultPosition = useMemo(() => ({ value: '', label: 'Ingen stilling' }), []);
	const defaultEmployer = useMemo(() => ({ value: '', label: 'Ingen arbejdsgiver' }), []);
	const { requestConfig, memberId, isMember } = useAuthenticatedCRMRequestConfig();
	const { data: member, error, isLoading: memberLoading } = useGetUser();
	const { mutateAsync, isLoading: isSubmitting, isError, isSuccess } = usePutMember({ request: requestConfig });
	const companies = useRef<CompanyDto[]>([]);
	const positionsRef = useRef<PositionDto[]>([]);

	const {
		data: positions,
		isLoading: isLoadingPositions,
	} = useGetPositions({ search: positionSearchTerm }, {
		query: {
			enabled: positionSearchTerm.length >= 3,
			onSettled: (val) => {
				positionsRef.current = val?.items || [];
			},
		},
		request: requestConfig,
	});

	const {
		data: employers,
		isLoading: isLoadingEmployers,
	} = useGetCompanyByFilter({ search: employerSearchTerm }, {
		query: {
			enabled: employerSearchTerm.length >= 3,
			onSettled: (val) => {
				companies.current = val?.items || [];
			},
		},
		request: requestConfig,
	});

	useEffect(() => {
		companies.current = employers?.items || [];
		positionsRef.current = positions?.items || [];
	}, [employers, employers?.items, positions, positions?.items]);

	const startDate = member?.employer?.startDate ? getDatePart(member.employer?.startDate) : '';
	const companyId = member?.employer?.companyId ? member.employer.companyId : '';
	const positionId = member?.positionId ? member.positionId : '';

	const {
		data: employerData,
		isLoading: employerIsLoading,
	} = useGetCompanyById(companyId, {
		query: {
			enabled: !!companyId,
		},
		request: requestConfig,
	});

	const {
		data: positionData,
		isLoading: positionIsLoading,
	} = useGetPositionById(positionId, {
		query: {
			enabled: !!positionId,
		},
		request: requestConfig,
	});

	const setEmployerSearchTextDebounced = useRef(
		debounce((searchText) => setEmployerSearchTerm(searchText), 100),
	).current;

	const setPositionSearchTextDebounced = useRef(
		debounce((searchText) => setPositionSearchTerm(searchText), 100),
	).current;

	const {
		getValues, setValue, control, setError, reset,
		handleSubmit,
		formState: { errors }, trigger,
	} = useForm<FormValues>({
		mode: 'onSubmit',
		defaultValues: {
			position: defaultPosition,
			employer: defaultEmployer,
			employmentDate: '',
			employmentAddress: '',
			employmentPostalCode: '',
		},
		resolver: yupResolver(schema),
		reValidateMode: 'onSubmit',
	});

	const renderCompanyOption = (employer: CompanyDto) => {
		const { name, address } = employer;
		const { addressLine, zipCode, city } = address;

		const labelParts = [
			name !== undefined ? name : '',
			addressLine !== undefined ? addressLine : '',
			zipCode !== undefined ? zipCode : '',
			city !== undefined ? city : '',
		];

		const label = labelParts.join(', ');

		return { value: employer.companyId, label };
	};

	useEffect(() => {
		if (positionData) {
			defaultPosition.value = positionData.positionId || '';
			defaultPosition.label = positionData.name || '';
			setValue('position', defaultPosition);
			setPositionSearchTerm(defaultPosition.label);
		}

		if (employerData) {
			defaultEmployer.value = employerData.companyId || '';
			defaultEmployer.label = renderCompanyOption(employerData).label || '';
			setValue('employer', defaultEmployer);
			setEmployerSearchTerm(defaultEmployer.label);
		}
		// Should not include the data since when search and the data updates it will set the input to the default value
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultEmployer, defaultPosition, employerData, positionData, setValue, startDate]);

	async function onSubmit(payload: FormValues) {
		try {
			if (!member) {
				throw new Error('Member data not ready');
			}

			const employerEmpty = payload.employer.value === '';
			const positionEmpty = payload.position.value === '';
			const newPosition = payload.position.label === payload.position.value;
			const newEmployer = payload.employer.label === payload.employer.value;

			await mutateAsync({
				memberId,
				data: {
					position: positionEmpty ? null : {
						positionId: !newPosition ? payload.position.value : null,
						positionName: newPosition ? payload.position.label : null,
					},
					employer: employerEmpty ? null : {
						companyId: !newEmployer ? payload.employer.value : null,
						name: newEmployer ? payload.employer.label : null,
						address: payload.employmentAddress || null,
						zipcode: payload.employmentPostalCode ? Number(payload.employmentPostalCode) : null,
						startDate: payload.employmentDate || '',
					},
				},
			});

			reset({
				position: defaultPosition,
				employer: defaultEmployer,
			});
			setDateVisible(false);
		} catch (err) {
			setError('root', {
				message: (err?.response?.data as ErrorResult | undefined)?.publicMessage || 'Der skete en fejl. Prøv igen senere.',
			});
		}
	}

	useEffect(() => {
		if (isSuccess) {
			setModalVisible(true);
		}
	}, [isSuccess]);

	const dataLoading = memberLoading || employerIsLoading || positionIsLoading;

	if (error) {
		throw crmError({ cause: error as unknown as Error });
	}

	const handleSearch = (inputText: string, event, fieldName?: 'employer' | 'position') => {
		if (event.action !== 'input-blur' && event.action !== 'menu-close' && event.action !== 'set-value') {
			if (fieldName === 'employer') {
				setEmployerSearchTextDebounced(inputText);
			} else if (fieldName === 'position') {
				setPositionSearchTextDebounced(inputText);
			}
		}
	};

	function shouldDisplayDate() {
		const employer = getValues('employer');
		// set date visible only if new employer was selected
		if ((!employerData || defaultEmployer.label !== employer?.label) && employer?.value !== '') {
			trigger('employer');
			setDateVisible(true);
		} else {
			setDateVisible(false);
		}
	}

	return (
		<Container className={styles.container}>
			{dataLoading ? (
				<ThemedSkeleton className={styles.skeleton_loader} />
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<FormWrapper>
						<div className={styles.banner_wrapper}>
							{(errors.position || errors.employmentAddress || errors.employmentPostalCode || errors.employmentPostalCode || isError) && <FormHeader type='error' message={isError ? 'Noget gik galt - prøv igen senere' : 'Der er fejl i et eller flere felter'} />}
							{(isSuccess && !(errors.position || errors.employmentAddress || errors.employmentPostalCode || errors.employmentPostalCode)) && <FormHeader type='success' message='Vi behandler dine ændringer' />}
						</div>
						<Form>
							<div className={styles.controller_container}>
								<Controller
									name='position'
									control={control}
									render={({ field, fieldState }) => {
										const positionsOptions = (positions?.items || [])
											.map((e) => ({ value: e.positionId as string, label: e.name }));

										const noPositionOption = { value: '', label: 'Ingen stilling' };

										return (
											<CreatableSelect
												{...field}
												value={field.value as (typeof positionsOptions)[number]}
												fieldState={fieldState}
												isLoading={isLoadingPositions}
												onInputChange={(inputText, event) => handleSearch(inputText, event, field.name)}
												noOptionalLabel
												onChange={(event) => {
													setPositionSearchTerm('');
													field.onChange(event);
												}}
												label='Stilling'
												options={[noPositionOption, ...positionsOptions]}
												dropdownIndicator={() => <Icon icon="search" size="24px" />}
												disabled={!isMember || isSubmitting}
												placeholder={' '}
											/>
										);
									}}
								/>
							</div>
							<div className={styles.controller_container}>
								<Controller
									name='employer'
									control={control}
									render={({ field, fieldState }) => {
										const values = getValues();
										const companyOptions = (employers?.items || []).map(renderCompanyOption);
										const noCompanyOption = { value: '', label: 'Ingen arbejdsgiver' };

										return (
											<div className={styles.employer_input}>
												<CreatableSelect
													{...field}
													value={field.value as (typeof companyOptions)[number]}
													onChange={(event) => {
														setEmployerSearchTerm('');
														field.onChange(event);
														shouldDisplayDate();
													}}
													fieldState={(!!values.employmentAddress && !!values.employmentPostalCode)
														? undefined
														: fieldState}
													isLoading={isLoadingEmployers}
													onInputChange={(inputText, event) => handleSearch(inputText, event, field.name)}
													noOptionalLabel
													label='Arbejdsgiver'
													options={[noCompanyOption, ...companyOptions]}
													dropdownIndicator={() => <Icon icon="search" size="24px" />}
													disabled={!isMember || isSubmitting}
													placeholder={' '}
												/>
											</div>
										);
									}}
								/>
							</div>
							{(errors.employer || errors.employmentAddress || errors.employmentPostalCode || (isSubmitting && (getValues('employer').value === getValues('employer').label && getValues('employer').value))) && (
								<>
									<Controller
										control={control}
										name="employmentAddress"
										render={({ field, fieldState }) => (
											<InputText
												fieldState={fieldState}
												className={styles.additional_work_input}
												label='Arbejdsgivers adresse'
												{...field}
												disabled={!isMember || isSubmitting}
												noOptionalLabel
											/>
										)}
									/>
									<Controller
										control={control}
										name="employmentPostalCode"
										render={({ field, fieldState }) => (
											<InputText
												fieldState={fieldState}
												className={styles.additional_work_input}
												label='Arbejdsgivers postnr.'
												{...field}
												disabled={!isMember || isSubmitting}
												noOptionalLabel
											/>
										)}
									/>
								</>
							)}
							{(errors.employer || dateVisible) &&
								<Controller
									control={control}
									name="employmentDate"
									render={({ field, fieldState }) => (
										<InputText
											type='date'
											fieldState={fieldState}
											label='Ansættelsesdato'
											{...field}
											noOptionalLabel
											disabled={!isMember || isSubmitting}
										/>
									)}
								/>
							}
						</Form>
					</FormWrapper>
					<FormFooter>
						<Button
							disabled={!isMember || isSubmitting}
							className={styles.button}
							type='submit'
							icon={isSubmitting ? { icon: 'spinner' } : { icon: 'checkmark' }}
						>
							Gem ændringer
						</Button>
					</FormFooter>
				</form>
			)}
			{isSuccess && (
				// Open Modal
				<Modal open={modalVisible}>
					<div className={styles.modal_content}>
						<h3>Tak for dine ændringer</h3>
						<p>
							Dine gamle oplysninger bliver stående til de nye oplysninger har været forbi en ansat. Det kan tage nogle dage,
							mens vi arbejder på sagen. Du behøver ikke gøre mere.
						</p>
						<Button onClick={() => setModalVisible(false)} className={styles.button} icon={{ icon: 'checkmark' }}>Okay</Button>
					</div>
				</Modal>
			)}
		</Container>
	);
}
